import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, IconButton, List, ListItem, ListItemText, Typography, Button, Autocomplete } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { parseCalendarEventsRows } from '../utils/calendarEventsExcel';
import { DEFAULT_EVENT_COLOR, EXCEL_IMPORT_DEFAULT_COLOR } from '../utils/calendarEventColors';

/** Fecha de inicio de un evento, con compatibilidad hacia atrás con el campo antiguo `date` (fecha única). */
function eventStart(ev) {
  return ev.startDate || ev.date || '';
}
function eventEnd(ev) {
  return ev.endDate || ev.date || ev.startDate || '';
}

const CURRENT_YEAR = new Date().getFullYear();
// Chile como valor inicial del selector; la lista completa se obtiene del servidor.
const DEFAULT_COUNTRY = { code: 'CL', name: 'Chile' };

/** Editor de la lista de feriados/eventos de un gráfico de calendario (agregar y quitar). */
const CalendarEventsEditor = ({ events = [], onChange, getAuthHeaders }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [color, setColor] = useState(DEFAULT_EVENT_COLOR);
  const endBeforeStart = Boolean(startDate && endDate && endDate < startDate);
  const [importYear, setImportYear] = useState(String(CURRENT_YEAR));
  const [importCountry, setImportCountry] = useState(DEFAULT_COUNTRY);
  const [countryOptions, setCountryOptions] = useState([DEFAULT_COUNTRY]);
  const [holidayColor, setHolidayColor] = useState(DEFAULT_EVENT_COLOR);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [importResult, setImportResult] = useState('');
  const fileInputRef = useRef(null);
  const [excelColor, setExcelColor] = useState(EXCEL_IMPORT_DEFAULT_COLOR);
  const [excelImporting, setExcelImporting] = useState(false);
  const [excelError, setExcelError] = useState('');
  const [excelResult, setExcelResult] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/holidays/countries', { headers: getAuthHeaders?.() })
      .then((res) => {
        if (cancelled) return;
        const countries = res.data?.countries;
        if (Array.isArray(countries) && countries.length > 0) {
          setCountryOptions(countries);
          // Re-seleccionar por code contra la lista real: si el default (Chile) no viniera
          // en esta lista, el value del Autocomplete quedaria apuntando a un objeto que no
          // matchea ninguna opcion.
          setImportCountry((prev) => countries.find((c) => c.code === prev?.code) || prev);
        }
      })
      .catch(() => {
        // Sin lista de paises, se sigue pudiendo importar con el default (Chile).
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const importHolidays = async () => {
    const year = parseInt(importYear, 10);
    if (!Number.isInteger(year) || !importCountry?.code) return;
    setImporting(true);
    setImportError('');
    setImportResult('');
    try {
      const res = await axios.get('/api/holidays', {
        params: { year, country: importCountry.code },
        headers: getAuthHeaders?.(),
      });
      const holidays = res.data?.holidays || [];
      // Evita duplicados si se importa el mismo año mas de una vez: mismo nombre + fecha.
      const existingKeys = new Set((events || []).map((e) => `${e.name}|${eventStart(e)}`));
      const newEvents = holidays
        .filter((h) => !existingKeys.has(`${h.name}|${h.date}`))
        .map((h) => ({
          id: `ev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          name: h.name,
          description: '',
          startDate: h.date,
          endDate: h.date,
          color: holidayColor,
        }));
      if (newEvents.length > 0) {
        onChange([...(events || []), ...newEvents]);
        setImportResult(`Se agregaron ${newEvents.length} feriados.`);
      } else {
        setImportResult(
          holidays.length > 0 ? 'Ese año ya estaba importado, no hay feriados nuevos.' : 'No se encontraron feriados para ese año.'
        );
      }
    } catch (err) {
      setImportError(err.response?.data?.error || 'No se pudieron importar los feriados');
    } finally {
      setImporting(false);
    }
  };

  const importFromExcelFile = async (file) => {
    if (!file) return;
    setExcelImporting(true);
    setExcelError('');
    setExcelResult('');
    try {
      const buffer = await file.arrayBuffer();
      // cellDates:true para que las celdas con formato fecha lleguen como Date, no como
      // el numero de serie interno de Excel (ver normalizeExcelDate en calendarEventsExcel.js).
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const firstSheet = workbook.SheetNames[0];
      const rows = firstSheet ? XLSX.utils.sheet_to_json(workbook.Sheets[firstSheet], { defval: '' }) : [];
      const { events: parsedEvents, skipped, totalRows } = parseCalendarEventsRows(rows);
      const existingKeys = new Set((events || []).map((e) => `${e.name}|${eventStart(e)}`));
      const newEvents = parsedEvents
        .filter((e) => !existingKeys.has(`${e.name}|${e.startDate}`))
        .map((e) => ({ ...e, color: excelColor }));
      if (newEvents.length > 0) {
        onChange([...(events || []), ...newEvents]);
      }
      const skippedNote = skipped > 0 ? ` (${skipped} fila${skipped === 1 ? '' : 's'} sin nombre o fecha válida, se ignoraron)` : '';
      if (newEvents.length > 0) {
        setExcelResult(`Se agregaron ${newEvents.length} eventos.${skippedNote}`);
      } else if (totalRows > 0 && skipped === totalRows) {
        // Ninguna fila matcheo columnas reconocibles: mensaje distinto a "ya estaban
        // importados", que sonaria contradictorio junto al aviso de filas ignoradas.
        setExcelResult('No se reconoció ninguna fila. Revisa que el Excel tenga columnas de Nombre y Fecha inicio.');
      } else {
        setExcelResult(`No se agregaron eventos nuevos (ya estaban importados).${skippedNote}`);
      }
    } catch {
      setExcelError('No se pudo leer el archivo. Revisa que sea un Excel/CSV válido.');
    } finally {
      setExcelImporting(false);
    }
  };

  const addEvent = () => {
    if (!name.trim() || !startDate || endBeforeStart) return;
    onChange([
      ...(events || []),
      {
        id: `ev-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        name: name.trim(),
        description: description.trim(),
        startDate,
        endDate: endDate || startDate,
        color,
      },
    ]);
    setName('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setColor(DEFAULT_EVENT_COLOR);
  };

  const removeEvent = (id) => {
    onChange((events || []).filter((e) => e.id !== id));
  };

  const sorted = [...(events || [])].sort((a, b) => eventStart(a).localeCompare(eventStart(b)));

  return (
    <Box
      sx={{
        width: 320,
        maxHeight: '75vh',
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        pr: 0.5,
      }}
    >
      <Typography variant="subtitle2">Feriados y eventos</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Año"
          type="number"
          size="small"
          value={importYear}
          onChange={(e) => setImportYear(e.target.value)}
          sx={{ width: 90 }}
        />
        <Autocomplete
          size="small"
          options={countryOptions}
          getOptionLabel={(c) => c.name || ''}
          isOptionEqualToValue={(a, b) => a.code === b.code}
          value={importCountry}
          onChange={(e, next) => setImportCountry(next)}
          disableClearable
          sx={{ flex: 1 }}
          renderInput={(params) => <TextField {...params} label="País" />}
        />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Color"
          type="color"
          size="small"
          value={holidayColor}
          onChange={(e) => setHolidayColor(e.target.value)}
          sx={{ width: 70 }}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={importHolidays}
          disabled={importing || !importCountry}
          sx={{ whiteSpace: 'nowrap', flex: 1 }}
        >
          {importing ? 'Importando…' : 'Importar feriados'}
        </Button>
      </Box>
      {importError && (
        <Typography variant="caption" color="error">
          {importError}
        </Typography>
      )}
      {importResult && !importError && (
        <Typography variant="caption" color="text.secondary">
          {importResult}
        </Typography>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = ''; // permite volver a elegir el mismo archivo
          importFromExcelFile(file);
        }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label="Color"
          type="color"
          size="small"
          value={excelColor}
          onChange={(e) => setExcelColor(e.target.value)}
          sx={{ width: 70 }}
          InputLabelProps={{ shrink: true }}
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => fileInputRef.current?.click()}
          disabled={excelImporting}
          sx={{ whiteSpace: 'nowrap', flex: 1 }}
        >
          {excelImporting ? 'Importando…' : 'Importar desde Excel'}
        </Button>
      </Box>
      {excelError && (
        <Typography variant="caption" color="error">
          {excelError}
        </Typography>
      )}
      {excelResult && !excelError && (
        <Typography variant="caption" color="text.secondary">
          {excelResult}
        </Typography>
      )}
      <List dense sx={{ maxHeight: 200, overflowY: 'auto', p: 0 }}>
        {sorted.length === 0 && (
          <Typography variant="caption" color="text.secondary">
            Sin eventos agregados
          </Typography>
        )}
        {sorted.map((ev) => {
          const start = eventStart(ev);
          const end = eventEnd(ev);
          const dateLabel = end && end !== start ? `${start} → ${end}` : start;
          return (
            <ListItem
              key={ev.id}
              disableGutters
              secondaryAction={
                <IconButton size="small" onClick={() => removeEvent(ev.id)} title="Quitar evento">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: ev.color || DEFAULT_EVENT_COLOR,
                  mr: 1,
                  flexShrink: 0,
                }}
                title="Color del evento"
              />
              <ListItemText
                primary={ev.name}
                secondary={
                  <>
                    {dateLabel}
                    {ev.description ? <><br />{ev.description}</> : null}
                  </>
                }
              />
            </ListItem>
          );
        })}
      </List>
      <TextField label="Nombre del evento" size="small" value={name} onChange={(e) => setName(e.target.value)} />
      <TextField
        label="Descripción (opcional)"
        size="small"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <TextField
        label="Fecha inicio"
        type="date"
        size="small"
        value={startDate}
        onChange={(e) => setStartDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label="Fecha fin (opcional)"
        type="date"
        size="small"
        value={endDate}
        onChange={(e) => setEndDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: startDate || undefined }}
        error={endBeforeStart}
        helperText={endBeforeStart ? 'La fecha fin no puede ser anterior a la fecha inicio' : ' '}
      />
      <TextField
        label="Color"
        type="color"
        size="small"
        value={color}
        onChange={(e) => setColor(e.target.value)}
        sx={{ width: 70 }}
        InputLabelProps={{ shrink: true }}
      />
      <Button variant="contained" size="small" onClick={addEvent} disabled={!name.trim() || !startDate || endBeforeStart}>
        Agregar evento
      </Button>
    </Box>
  );
};

export default CalendarEventsEditor;
